// src/pages/VolunteerOpportunitiesPage.js
// Updated: 08/25/2025 08:25 PM

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import {
  collection, query, onSnapshot, getDocs, doc, where, setDoc, serverTimestamp, deleteDoc
} from 'firebase/firestore';
import {
  Container, Typography, CircularProgress, Box, Button, Card, CardContent,
  CardActions, Grid, List, ListItem, ListItemText, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { isAfter, format } from 'date-fns'; // Import format from date-fns

function VolunteerOpportunitiesPage() {
    const { userProfile, currentUser } = useAuth();
    const navigate = useNavigate();
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
    const [selectedOpportunity, setSelectedOpportunity] = useState(null);
    const [selectedPositionId, setSelectedPositionId] = useState('');
    const [positions, setPositions] = useState([]);
    const [error, setError] = useState('');

    const isLeadership = userProfile?.role === 'Leadership' || userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

    useEffect(() => {
        if (!userProfile?.churchId) {
            setLoading(false);
            return;
        }

        const opportunitiesColRef = collection(db, 'churches', userProfile.churchId, 'volunteerOpportunities');
        const q = query(opportunitiesColRef, where('date', '>', new Date()));
        const unsubscribe = onSnapshot(q, async (snapshot) => {
            const fetchedOpportunities = [];
            for (const doc of snapshot.docs) {
                const data = doc.data();
                const positionsSnapshot = await getDocs(collection(doc.ref, 'positions'));
                const positionsData = positionsSnapshot.docs.map(p => ({
                    id: p.id,
                    ...p.data(),
                }));
                fetchedOpportunities.push({ id: doc.id, ...data, positions: positionsData });
            }
            setOpportunities(fetchedOpportunities);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching volunteer opportunities: ", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userProfile]);

    const handleSignUpClick = (opportunity) => {
        setSelectedOpportunity(opportunity);
        setPositions(opportunity.positions);
        setSelectedPositionId('');
        setIsSignUpModalOpen(true);
    };

    const handleSignUp = async () => {
        if (!selectedPositionId || !selectedOpportunity || !currentUser) {
            setError('Please select a position to sign up.');
            return;
        }

        const userEmail = userProfile?.email || currentUser?.email;
        if (!userEmail) {
            setError('Your email is not available. Please check your profile and try again.');
            return;
        }

        const positionToSign = positions.find(p => p.id === selectedPositionId);
        if (positionToSign.signedUpUids?.includes(currentUser.uid)) {
            setError('You have already signed up for this position.');
            return;
        }

        if (positionToSign.signedUpUids?.length >= positionToSign.required) {
            setError('This position is already full.');
            return;
        }

        try {
            const signUpsRef = collection(db, 'churches', userProfile.churchId, 'volunteerOpportunities', selectedOpportunity.id, 'signUps');
            const newSignUpRef = doc(signUpsRef, currentUser.uid);
            await setDoc(newSignUpRef, {
                positionId: selectedPositionId,
                signedUpAt: serverTimestamp(),
                name: `${userProfile.firstName} ${userProfile.lastName}`,
                email: userEmail,
            });
            setIsSignUpModalOpen(false);
            setSelectedOpportunity(null);
            setSelectedPositionId('');
            alert('Successfully signed up for the position!');
        } catch (e) {
            console.error("Error signing up for volunteer opportunity:", e);
            setError('Failed to sign up. Please try again.');
        }
    };
    
    const handleAddOpportunity = () => {
        navigate('/volunteer/new');
    };

    const handleCancelSignUp = async (oppId, positionId) => {
        if (!currentUser) return;
    
        const signupRef = doc(db, `churches/${userProfile.churchId}/volunteerOpportunities/${oppId}/signUps`, currentUser.uid);
    
        try {
            await deleteDoc(signupRef);
            // Firestore trigger will handle removing from position.signedUpUids
            alert('Sign-up cancelled successfully!');
        } catch (error) {
            console.error("Error cancelling volunteer sign-up:", error);
            alert(`Failed to cancel sign-up: ${error.message}`);
        }
    };
    
    // *** FIX: New function to format date and time range ***
    const formatDateTime = (start, end) => {
        if (!start) return '';
        const startDate = start.toDate();
        const datePart = format(startDate, 'MMM d, yyyy');
        const startTimePart = format(startDate, 'p');

        if (end) {
            const endTimePart = format(end.toDate(), 'p');
            return `${datePart}, ${startTimePart} - ${endTimePart}`;
        }
        return `${datePart}, ${startTimePart}`;
    };


    if (loading) {
        return <CircularProgress />;
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">Volunteer Opportunities</Typography>
                {isLeadership && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddOpportunity}>
                        Add Opportunity
                    </Button>
                )}
            </Box>

            {opportunities.length === 0 && (
                <Typography>No upcoming volunteer opportunities available at this time.</Typography>
            )}

            <Grid container spacing={3}>
                {opportunities.map((opportunity) => {
                    const isSignedUp = opportunity.positions.some(p => p.signedUpUids.includes(currentUser.uid));
                    const isFull = opportunity.positions.every(p => p.signedUpUids.length >= p.required);
                    const isPast = opportunity.date?.toDate() && isAfter(new Date(), opportunity.date.toDate());
                    
                    const canManage = isLeadership || opportunity.contactPersonId === currentUser.uid;

                    return (
                        <Grid item key={opportunity.id} xs={12} sm={6} md={4}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography gutterBottom variant="h5" component="h2">
                                        {opportunity.title}
                                    </Typography>
                                    {/* *** FIX: Use the new formatting function *** */}
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Date: {formatDateTime(opportunity.date, opportunity.endTime)}
                                    </Typography>
                                    
                                    {opportunity.contactPersonName && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <ContactPhoneIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                Contact: {opportunity.contactPersonName}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {opportunity.description}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle1">Positions:</Typography>
                                        <List dense>
                                            {opportunity.positions.map((position) => (
                                                <ListItem key={position.id} sx={{ display: 'block', pl: 0 }}>
                                                    <ListItemText
                                                        primary={position.name}
                                                        secondary={`${position.signedUpUids?.length || 0} / ${position.required} filled`}
                                                    />
                                                    {position.description && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ pl: 2, fontStyle: 'italic' }}>
                                                            - {position.description}
                                                        </Typography>
                                                    )}
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                    <Box>
                                        {isSignedUp ? (
                                            <Button onClick={() => handleCancelSignUp(opportunity.id, opportunity.positions.find(p => p.signedUpUids.includes(currentUser.uid))?.id)} disabled={isPast}>
                                                Cancel Sign-up
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                onClick={() => handleSignUpClick(opportunity)}
                                                disabled={isFull || isPast}
                                            >
                                                {isPast ? 'Event Passed' : isFull ? 'Full' : 'Sign Up'}
                                            </Button>
                                        )}
                                    </Box>
                                    <Box>
                                        {isLeadership && (
                                            <Button
                                                size="small"
                                                onClick={() => navigate(`/volunteer/${opportunity.id}/edit`)}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                        {canManage && (
                                            <Button
                                                size="small"
                                                onClick={() => navigate(`/volunteer/${userProfile.churchId}/${opportunity.id}/manage`)}
                                            >
                                                Manage
                                            </Button>
                                        )}
                                    </Box>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Dialog open={isSignUpModalOpen} onClose={() => setIsSignUpModalOpen(false)}>
                <DialogTitle>Sign up for {selectedOpportunity?.title}</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Select Position</InputLabel>
                        <Select
                            value={selectedPositionId}
                            label="Select Position"
                            onChange={(e) => setSelectedPositionId(e.target.value)}
                            renderValue={(value) => positions.find(p => p.id === value)?.name || ''}
                        >
                            {positions.map(position => (
                                <MenuItem 
                                    key={position.id} 
                                    value={position.id}
                                    disabled={position.signedUpUids?.length >= position.required}
                                >
                                    <ListItemText
                                        primary={`${position.name} (${position.signedUpUids?.length || 0}/${position.required} filled)`}
                                        secondary={position.description || null}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsSignUpModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleSignUp} variant="contained">Confirm Sign Up</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default VolunteerOpportunitiesPage;